import { useState, createRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import { Link } from 'components/UI/Link/Link';
import { addUpsellItemToCart } from 'services/UpsellsService';
import { ReactComponent as IconApproved } from 'assets/icons/svgs/icon-approved.svg';
import { UpsellEventInformation } from './UpsellEventInformation';
import { BookPanelSinglePriceType } from './BookPanelSinglePriceType';
import { BookPanelMultiplePriceTypes } from './BookPanelMultiplePriceTypes';
import { UpsellsCartTotal } from './UpsellsCartTotal';

import './UpsellsPage.scss';

export const UpsellsPage = (props) => {
  const { PreviousStepUrl, PreviousStepName, DescriptionText, UpsellItems, Order, NextStepUrl } =
    props;
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [experienceData, setExperienceData] = useState(Order);

  const handleAddUpsellToCart = async (requestPayload, addedToCartMessageRef) => {
    setIsProcessing(true);

    try {
      trackEvent('add_to_cart', { upsell: requestPayload });
      const response = await addUpsellItemToCart(requestPayload);

      if (response?.Success) {
        setExperienceData(response.Order);
        addedToCartMessageRef.innerHTML = ReactDOMServer.renderToString(
          <>
            <IconApproved className="icon fill-teal" />
            {response?.NumberOfUpsellSelected} added to cart
          </>
        );
        setErrorMessage('');
      } else if (response?.ErrorMessage) {
        setErrorMessage(response?.ErrorMessage);
      } else {
        setErrorMessage('Something went wrong.  Please try again');
      }

      setIsProcessing(false);
    } catch (e) {
      setIsProcessing(false);
      setErrorMessage(e.message);
    }
  };

  return (
    <section className="upsells-page" data-testid="upsells-page">
      <div className="container">
        <div className="breadcrumbs">
          <Link
            href={PreviousStepUrl}
            variant="secondary"
            extendClass="link-breadcrumb link-back-chevr"
            extendTestId="breadcrumb"
          >
            Back to {PreviousStepName}
          </Link>
        </div>

        <div className="row">
          <div className="col-sm-9">
            <h2 className="h4">Add to your experience at Arts Centre Melbourne</h2>
            <p>{DescriptionText}</p>
          </div>
        </div>
      </div>

      <div className="container">
        {errorMessage && (
          <div className="callout-row">
            <div className="callout-container callout-error">
              <p>{errorMessage}</p>
            </div>
          </div>
        )}

        {UpsellItems?.map((upsellItem, index) => {
          const isSinglePriceType =
            upsellItem.Performance.PriceTypes.length === 1 &&
            upsellItem.UpsellType !== 'Performance';
          const defaultNumberOfUpsells = upsellItem.NumberOfUpsellSelected;

          const bookPanelProps = {
            handleAddUpsellToCart,
            isProcessing,
            errorMessage,
            numberOfUpsellSelected: defaultNumberOfUpsells,
            msgAddedRef: createRef(),
          };

          const bookPanelPriceTypes = isSinglePriceType ? (
            <BookPanelSinglePriceType Performance={upsellItem.Performance} {...bookPanelProps} />
          ) : (
            <BookPanelMultiplePriceTypes {...upsellItem} {...bookPanelProps} />
          );

          return (
            <div className="upsells-page__booking-panel" key={index}>
              <div className="panel panel-default panel-atye">
                <UpsellEventInformation {...upsellItem} errorMessage={errorMessage} />
                <div className="panel-footer" data-testid="upsell-panel-footer">
                  {bookPanelPriceTypes}
                </div>
              </div>
            </div>
          );
        })}

        <UpsellsCartTotal Order={experienceData} />
      </div>

      <div className="container">
        <div className="row">
          <div className="col-sm-4 col-sm-offset-8 col-md-3 col-md-offset-9">
            <a href={NextStepUrl} className="btn btn-primary btn-cta">
              Proceed to checkout
            </a>
          </div>
        </div>
        <div className="text-complement">
          <p>
            Need to find more tickets? <br className="visible-xs" />
            <a href="/whats-on" className="link-bold">
              Continue browsing events
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

UpsellsPage.propTypes = {
  PreviousStepUrl: PropTypes.string,
  PreviousStepName: PropTypes.string,
  DescriptionText: PropTypes.string,
  UpsellItems: PropTypes.array,
  Order: PropTypes.object,
  NextStepUrl: PropTypes.string,
};

export const UpsellsPageSC = (props) => {
  const { sitecoreData } = props;
  return <UpsellsPage {...sitecoreData} />;
};

UpsellsPageSC.propTypes = {
  sitecoreData: PropTypes.object,
};
